/**
 * AdminAnalyticsTab Component - Shell
 * Tab navigation, period selector, data fetching, report generation
 * Delegates view rendering to sub-components
 */
import { useEffect, useState } from 'preact/hooks';
import { authenticatedFetch } from '../../../services/authService.js';
import { AnalyticsAiSurfaceSpendView } from './AnalyticsAiSurfaceSpendView.jsx';
import { AnalyticsAiUsageView } from './AnalyticsAiUsageView.jsx';
import { AnalyticsAnonymousTrafficView } from './AnalyticsAnonymousTrafficView.jsx';
import { AnalyticsApiView } from './AnalyticsApiView.jsx';
import { AnalyticsCellAccessView } from './AnalyticsCellAccessView.jsx';
import { AnalyticsDashboardView } from './AnalyticsDashboardView.jsx';
import { AnalyticsScrapingView } from './AnalyticsScrapingView.jsx';
import { AnalyticsTrafficView } from './AnalyticsTrafficView.jsx';
import { AnalyticsFunnelTrendsView } from './AnalyticsFunnelTrendsView.jsx';
import { getApiBase } from './shared/formatters.js';
import { Icons } from './shared/Icons.jsx';

export function AdminAnalyticsTab() {
  const [activeView, setActiveView] = useState('dashboard');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [periodDays, setPeriodDays] = useState(7);
  const [periodDaysTrends, setPeriodDaysTrends] = useState(90);
  const [bucket, setBucket] = useState('week'); // 'day' | 'week' — only for Funnel Trends
  const [excludeAdmin, setExcludeAdmin] = useState(true);
  const [reportStatus, setReportStatus] = useState(null);

  // Deep-link support: open a specific sub-view via ?view=<id> (e.g. the
  // "Open Funnel Trends" button in the Teams briefing). Read once on mount.
  useEffect(() => {
    const v = new URLSearchParams(window.location.search).get('view');
    // Legacy redirect: the "Lead Funnel" tab was merged into the Dashboard.
    if (v === 'lead-funnel') { setActiveView('dashboard'); return; }
    // Legacy alias: the AI view's id is 'ai-usage'; 'ai-chat' was listed as
    // known but matched no view, so such a link rendered an empty pane.
    if (v === 'ai-chat') { setActiveView('ai-usage'); return; }
    const known = ['dashboard', 'funnel-trends', 'traffic', 'api',
                   'cell-access', 'scraping', 'anonymous-traffic',
                   'ai-usage', 'ai-surface-spend'];
    if (v && known.includes(v)) setActiveView(v);
  }, []);

  // Fetch analytics data (preview + funnel in parallel)
  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const apiBase = getApiBase();
      // report/preview → traffic + API KPIs (Traffic/API tabs also consume this).
      // funnel/dashboard-summary → the canonical funnel snapshot (SSoT, same
      // numbers as the Funnel Trends tab). Both run in parallel; the snapshot is
      // best-effort so a Plausible/DB hiccup never blanks the whole dashboard.
      const [previewRes, snapshotRes] = await Promise.all([
        authenticatedFetch(`${apiBase}/admin/analytics/report/preview?period_days=${periodDays}`),
        authenticatedFetch(`${apiBase}/admin/analytics/funnel/dashboard-summary?period_days=${periodDays}&exclude_admin=${excludeAdmin}`).catch(() => null),
      ]);

      if (previewRes.ok) {
        const data = await previewRes.json();
        setAnalyticsData(data);
      } else {
        setError('Failed to load analytics data');
      }

      if (snapshotRes?.ok) {
        const snap = await snapshotRes.json();
        setSnapshot(snap);
      } else {
        setSnapshot(null);
      }
    } catch (err) {
      console.error('Analytics fetch error:', err);
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [periodDays, excludeAdmin]);

  // Generate report
  const handleGenerateReport = async () => {
    setReportStatus('generating');
    try {
      const response = await authenticatedFetch(
        `${getApiBase()}/admin/analytics/report/generate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            period_days: periodDays,
          }),
        }
      );

      if (response.ok) {
        setReportStatus('success');
        setTimeout(() => setReportStatus(null), 5000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Report generation failed:', response.status, errorData);
        setReportStatus('error');
        setTimeout(() => setReportStatus(null), 5000);
      }
    } catch (err) {
      console.error('Report generation error:', err);
      setReportStatus('error');
      setTimeout(() => setReportStatus(null), 5000);
    }
  };

  const views = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'funnel-trends', label: 'Funnel Trends' },
    { id: 'traffic', label: 'Traffic' },
    { id: 'api', label: 'API Performance' },
    { id: 'cell-access', label: 'Cell Access' },
    { id: 'scraping', label: 'Scraping Detection' },
    { id: 'anonymous-traffic', label: 'Anonymous Traffic' },
    { id: 'ai-usage', label: 'AI Usage' },
    { id: 'ai-surface-spend', label: 'AI Spend by Feature' },
  ];

  const renderActiveView = () => {
    if (activeView === 'ai-usage') {
      return <AnalyticsAiUsageView periodDays={periodDays} />;
    }

    // Per-`surface` spend. Uses the same period selector as the other views —
    // the backend's range_days/bucket pair, no second range control.
    if (activeView === 'ai-surface-spend') {
      return <AnalyticsAiSurfaceSpendView periodDays={periodDays} />;
    }

    if (activeView === 'cell-access') {
      return <AnalyticsCellAccessView periodDays={periodDays} />;
    }

    if (activeView === 'scraping') {
      return <AnalyticsScrapingView periodDays={periodDays} />;
    }

    // Fetches its own data — must stay in this early-return block, above the
    // `analyticsData` guard below, or a failed report/preview call would blank
    // a view that does not depend on it.
    if (activeView === 'anonymous-traffic') {
      return <AnalyticsAnonymousTrafficView periodDays={periodDays} />;
    }

    if (activeView === 'funnel-trends') {
      return (
        <AnalyticsFunnelTrendsView
          periodDays={periodDaysTrends}
          bucket={bucket}
          excludeAdmin={excludeAdmin}
        />
      );
    }

    if (!analyticsData) return null;

    switch (activeView) {
      case 'dashboard':
        return <AnalyticsDashboardView data={analyticsData} snapshot={snapshot} periodDays={periodDays} onNavigate={setActiveView} />;
      case 'traffic':
        return <AnalyticsTrafficView data={analyticsData} periodDays={periodDays} />;
      case 'api':
        return <AnalyticsApiView data={analyticsData} periodDays={periodDays} />;
      default:
        return null;
    }
  };

  return (
    <div className="admin-analytics-tab">
      {/* Header with controls */}
      <div className="analytics-header">
        <div className="analytics-nav">
          {views.map((view) => (
            <button
              key={view.id}
              className={`analytics-nav-btn ${activeView === view.id ? 'active' : ''}`}
              onClick={() => setActiveView(view.id)}
            >
              <span>{view.label}</span>
            </button>
          ))}
        </div>

        <div className="analytics-controls">
          {/* Period selector — bound to active view (trends has its own range) */}
          {activeView === 'funnel-trends' ? (
            <>
              <select
                className="analytics-period-select"
                value={periodDaysTrends}
                onChange={(e) => setPeriodDaysTrends(parseInt(e.target.value))}
              >
                <option value="30">Last 30 Days</option>
                <option value="90">Last 90 Days</option>
                <option value="180">Last 180 Days</option>
                <option value="365">Last 365 Days</option>
              </select>
              <select
                className="analytics-period-select"
                value={bucket}
                onChange={(e) => setBucket(e.target.value)}
                title="Bucket size"
              >
                <option value="day">Daily</option>
                <option value="week">Weekly</option>
              </select>
            </>
          ) : (
            <select
              className="analytics-period-select"
              value={periodDays}
              onChange={(e) => setPeriodDays(parseInt(e.target.value))}
            >
              <option value="7">Last 7 Days</option>
              <option value="14">Last 14 Days</option>
              <option value="30">Last 30 Days</option>
              <option value="90">Last 90 Days</option>
            </select>
          )}

          {/* Admin account filter toggle */}
          <label className="analytics-toggle" title="Exclude Batemo employee accounts (@batemo.de / @batemo.com) and admin-created accounts">
            <input
              type="checkbox"
              checked={excludeAdmin}
              onChange={(e) => setExcludeAdmin(e.target.checked)}
            />
            <span className="analytics-toggle-label">Exclude admin accounts</span>
          </label>

          {/* Refresh button */}
          <button
            className="analytics-refresh-btn"
            onClick={fetchAnalytics}
            disabled={loading}
            title="Refresh data"
          >
            <span className={loading ? 'spinning' : ''}>{Icons.refresh}</span>
          </button>

          {/* Generate Report Button */}
          <button
            className={`analytics-report-btn ${reportStatus || ''}`}
            onClick={handleGenerateReport}
            disabled={reportStatus === 'generating'}
          >
            {reportStatus === 'generating' ? (
              <>
                <span className="spinner-small"></span>
                <span>Generating…</span>
              </>
            ) : reportStatus === 'success' ? (
              <>
                <span className="icon-success">{Icons.check}</span>
                <span>Report sent</span>
              </>
            ) : reportStatus === 'error' ? (
              <>
                <span className="icon-error">{Icons.x}</span>
                <span>Error</span>
              </>
            ) : (
              <>
                <span>{Icons.mail}</span>
                <span>Generate report</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="analytics-content">
        {loading && !analyticsData && activeView !== 'funnel-trends' ? (
          <div className="analytics-loading">
            <div className="spinner"></div>
            <p>Loading analytics data…</p>
          </div>
        ) : error && activeView !== 'funnel-trends' ? (
          <div className="analytics-error">
            <span>{Icons.alertTriangle}</span>
            <p>{error}</p>
            <button onClick={fetchAnalytics}>Retry</button>
          </div>
        ) : (
          renderActiveView()
        )}
      </div>
    </div>
  );
}
